import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { findingsApi, type FindingItem, type LibraryFindings } from '@/services/api';

const TYPE_LABEL: Record<string, string> = {
  BUYER_CONCERN: 'concern',
  OBJECTION: 'objection',
  BENEFIT: 'benefit',
  PRODUCT_FEATURE: 'feature',
  CLAIM: 'claim',
  OFFER: 'offer',
  PROOF: 'proof',
  CERTIFICATION: 'certification',
  WARRANTY_TERM: 'warranty',
  PRICE_POINT: 'price',
};

function Bar({ value, max, color }: { value: number; max: number; color: string }) {
  return (
    <div className="h-1.5 rounded-full bg-slate-100 w-24">
      <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, (100 * value) / Math.max(1, max))}%`, background: color }} />
    </div>
  );
}

function Row({ item, max, mode }: { item: FindingItem; max: number; mode: 'reviews' | 'ads' | 'both' }) {
  return (
    <li className="py-1.5">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-navy-900">{item.label}</span>
        <span className="text-[10px] text-slate-400">{TYPE_LABEL[item.type] ?? item.type.toLowerCase()}</span>
        <span className="ml-auto flex items-center gap-2 text-[11px] text-slate-500">
          {mode !== 'ads' && (
            <>
              <Bar value={item.reviews} max={max} color="#2dd4bf" /> {item.reviews}
            </>
          )}
          {mode !== 'reviews' && (
            <>
              <Bar value={item.ads} max={max} color="#a78bfa" /> {item.ads}
            </>
          )}
        </span>
      </div>
      {(item.emphasis && item.emphasis !== 'balanced') || item.variants.length ? (
        <div className="text-[11px] text-slate-400">
          {item.emphasis && item.emphasis !== 'balanced' && <span className="text-amber-700 mr-2">{item.emphasis}</span>}
          {item.variants.length > 0 && <>also: {item.variants.slice(0, 3).join(', ')}</>}
        </div>
      ) : null}
    </li>
  );
}

function Column({
  title,
  hint,
  items,
  mode,
}: {
  title: string;
  hint: string;
  items: FindingItem[];
  mode: 'reviews' | 'ads' | 'both';
}) {
  const max = Math.max(1, ...items.map((i) => Math.max(i.reviews, i.ads)));
  return (
    <section className="rounded-xl border border-slate-200 p-3">
      <h3 className="text-sm font-medium text-navy-900">{title}</h3>
      <p className="text-[11px] text-slate-500 mb-2">{hint}</p>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400">Nothing yet.</p>
      ) : (
        <ul className="divide-y divide-slate-50 max-h-[520px] overflow-auto pr-1">
          {items.map((i) => (
            <Row key={`${i.type}:${i.label}`} item={i} max={max} mode={mode} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** What the library's extracted graph says, split by source: buyer reviews vs competitor ads. */
export function DocketFindingsTab({ docketId }: { docketId: string }) {
  const [data, setData] = useState<LibraryFindings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    findingsApi
      .get(docketId)
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [docketId]);
  useEffect(load, [load]);

  if (error) {
    return <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>;
  }
  if (!data) {
    return (
      <div className="flex items-center text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin mr-2" /> Reading the library&apos;s graph…
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <p className="text-xs text-slate-500 max-w-3xl">
          Counted from the Knowledge Explorer: how many review passages and how many competitor-ad passages mention each
          thing ({data.documents_read} of {data.documents} documents read, similar wordings folded by {data.merged_by}).
          <span className="ml-1 inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: '#2dd4bf' }} /> reviews
            <span className="inline-block w-2 h-2 rounded-full ml-2" style={{ background: '#a78bfa' }} /> ads
          </span>
        </p>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-navy-900">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
        <Column
          title={`Unanswered (${data.counts.unanswered})`}
          hint="Buyers raise these in reviews; no competitor ad addresses them. Openings for a brief."
          items={data.unanswered}
          mode="reviews"
        />
        <Column
          title={`Contested (${data.counts.contested})`}
          hint="Both buyers and ads talk about these. Flags where ads over- or under-weight what buyers say."
          items={data.contested}
          mode="both"
        />
        <Column
          title={`Category conventions (${data.counts.conventions})`}
          hint="What competitor ads argue: offers, proof, certifications, warranty terms, price points."
          items={data.conventions}
          mode="ads"
        />
      </div>
    </div>
  );
}
